import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const COLORS = ['#F6AA19', '#31313D', '#10B981', '#3B82F6', '#FCCD81', '#8B8383', '#8B5CF6', '#14B8A6', '#EF4444', '#84CC16', '#06B6D4', '#EC4899'];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
        <p className="text-sm font-medium text-slate-900">{payload[0].name}</p>
        <p className="text-sm text-slate-600 font-mono-data">
          {formatCurrency(payload[0].value)}
        </p>
        <p className="text-xs text-slate-500">
          {((payload[0].value / payload[0].payload.total) * 100).toFixed(1)}%
        </p>
      </div>
    );
  }
  return null;
};

const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value, total }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const percent = ((value / total) * 100).toFixed(0);
  
  if (percent < 10) return null; // Don't show label if too small
  
  return (
    <text
      x={x}
      y={y}
      fill="#ffffff"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize="11"
      fontWeight="600"
      style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.7)' }}
    >
      {percent}%
    </text>
  );
};

// Improved legend component with better layout for many items
const ImprovedLegend = ({ data, total }) => {
  // Sort by value descending
  const sortedData = [...data].sort((a, b) => b.value - a.value);
  const hasMany = sortedData.length > 4;
  
  return (
    <div className={`mt-3 ${hasMany ? 'max-h-[140px] overflow-y-auto pr-1' : ''}`}>
      <div className="flex flex-col gap-1.5">
        {sortedData.map((item, index) => {
          const percent = ((item.value / total) * 100).toFixed(1);
          const colorIndex = data.findIndex(d => d.name === item.name);
          return (
            <div 
              key={`legend-${index}`} 
              className="flex items-center gap-2 py-1 px-1 rounded hover:bg-slate-50 transition-colors"
            >
              <span
                className="w-3 h-3 rounded-sm flex-shrink-0"
                style={{ backgroundColor: COLORS[colorIndex % COLORS.length] }}
              />
              <span className="text-xs text-slate-700 flex-1 min-w-0" title={item.name}>
                <span className="block truncate">{item.name}</span>
              </span>
              <span className="text-xs font-semibold text-slate-900 font-mono-data flex-shrink-0">
                {percent}%
              </span>
              <span className="text-[10px] text-slate-500 font-mono-data flex-shrink-0 w-16 text-right">
                {formatCurrency(item.value)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function DonutChart({ data, title }) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const dataWithTotal = data.map(item => ({ ...item, total }));

  if (!data || data.length === 0 || total === 0) {
    return (
      <div className="chart-container h-[360px] flex flex-col" data-testid={`chart-${title?.toLowerCase().replace(/\s/g, '-')}`}>
        <h3 className="text-lg font-semibold text-slate-800 font-['Outfit'] mb-4">{title}</h3>
        <div className="flex-1 flex items-center justify-center text-slate-400">
          Sin datos disponibles
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container h-auto flex flex-col" data-testid={`chart-${title?.toLowerCase().replace(/\s/g, '-')}`}>
      <h3 className="text-lg font-semibold text-slate-800 font-['Outfit'] mb-2">{title}</h3>
      <div className="text-center text-sm text-slate-500 mb-1">
        Total: <span className="font-mono-data font-semibold text-slate-700">{formatCurrency(total)}</span>
      </div>
      
      {/* Chart */}
      <div className="h-[160px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={dataWithTotal}
              cx="50%"
              cy="50%"
              innerRadius={45}
              outerRadius={70}
              paddingAngle={2}
              dataKey="value"
              labelLine={false}
              label={<CustomLabel />}
            >
              {dataWithTotal.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      
      {/* Improved Legend */}
      <ImprovedLegend data={data} total={total} />
    </div>
  );
}
